import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Aboutbanner from "../About/Aboutbanner";
import Callin from "../About/Callin";
import Infiry from "../About/Infiry";
import Divser from "../About/Divser";
import ServiceReview from "../Services/ServiceReview";
import Youtube from "../Services/Youtube";
import { movetop } from "../../fun";
import { blogs, BlogItem } from "../../data/blogs";
import { FaCalendarAlt, FaUserEdit, FaFolder } from "react-icons/fa";

function BlogDetailMain({ blog }: { blog: BlogItem }) {
  // Related blogs matching same category or other blogs (excluding current blog)
  const relatedBlogs = blogs
    .filter((item) => item.slug !== blog.slug)
    .slice(0, 2);

  return (
    <div className="space-y-6">
      {/* Blog Article Container */}
      <article className="bg-white p-5 md:p-8 rounded-xl shadow-sm border border-gray-100">
        {/* Meta Info Header */}
        <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-gray-500 mb-4 pb-3 border-b border-gray-100">
          <div className="flex items-center space-x-1.5 bg-blue-50 text-blue-800 font-semibold px-3 py-1 rounded-full">
            <FaFolder className="text-blue-600" />
            <span>{blog.category}</span>
          </div>
          {blog.author && (
            <div className="flex items-center space-x-1.5">
              <FaUserEdit className="text-gray-400" />
              <span>{blog.author}</span>
            </div>
          )}
          {blog.date && (
            <div className="flex items-center space-x-1.5">
              <FaCalendarAlt className="text-gray-400" />
              <span>{blog.date}</span>
            </div>
          )}
        </div>

        {/* Dynamic Blog Content */}
        <div className="prose prose-blue max-w-none">
          {blog.content}
        </div>
      </article>

      {/* Related Blogs Section */}
      {relatedBlogs.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50/70 to-indigo-50/70 p-6 rounded-xl border border-blue-100 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4" style={{ color: "#00263e" }}>
            Related Articles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {relatedBlogs.map((item) => (
              <Link
                key={item.id}
                to={`/${item.slug}`}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="h-36 overflow-hidden rounded-lg mb-3">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                    {item.category}
                  </span>
                  <h4 className="font-bold text-gray-900 text-base mt-2 line-clamp-2 hover:text-blue-600 transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                    {item.excerpt}
                  </p>
                </div>
                <div className="mt-4 pt-2 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-blue-700">
                  <span>Read Article</span>
                  <span>→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BlogNotFound() {
  return (
    <div className="bg-white p-8 md:p-12 rounded-xl shadow-sm border border-gray-100 text-center my-8">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Blog Post Not Found</h2>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        The requested article could not be found or may have been moved.
      </p>
      <Link
        to="/blog"
        className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-sm"
      >
        Back to All Blogs
      </Link>
    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    movetop();
  }, [slug]);

  const blog = blogs.find((item) => item.slug === slug);

  if (!blog) {
    type NotFoundElem = {
      oneBody?: JSX.Element;
      twoBody?: JSX.Element;
      threeBody?: JSX.Element;
    };
    const notFoundElemnt: NotFoundElem = {
      oneBody: <BlogNotFound />,
      twoBody: <Infiry />,
      threeBody: <Callin />,
    };

    return (
      <>
        <Helmet>
          <title>Blog Not Found | ELV Health Care</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Aboutbanner name="Blog" banner="../photo/sam3.jpg" />
        <div className="pt-6 pb-0"></div>
        <Divser elemnt={notFoundElemnt} />
      </>
    );
  }

  type elem = {
    oneBody?: JSX.Element;
    twoBody?: JSX.Element;
    threeBody?: JSX.Element;
  };

  const elemnt: elem = {
    oneBody: <BlogDetailMain blog={blog} />,
    twoBody: <Infiry />,
    threeBody: <Callin />,
  };

  const seoTitle = blog.seoTitle || `${blog.title} | ELV Health Care`;
  const metaDesc = blog.metaDescription || blog.excerpt;
  const canonicalUrl = `https://elvhealthcare.com/${blog.slug}`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        {blog.keywords && <meta name="keywords" content={blog.keywords} />}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        {blog.image && <meta property="og:image" content={`https://elvhealthcare.com/${blog.image.replace("../", "")}`} />}
        <meta property="og:type" content="article" />

        {/* Dynamic JSON-LD Schema */}
        {blog.schemaGraph && (
          <script type="application/ld+json">
            {JSON.stringify(blog.schemaGraph)}
          </script>
        )}
      </Helmet>

      <Aboutbanner name={blog.category ? `Blog: ${blog.category}` : "Blog Details"} banner="../photo/sam3.jpg" />
      <div className="pt-6 pb-0"></div>
      <Divser elemnt={elemnt} />
      <ServiceReview />
      <Youtube />
    </>
  );
}
